import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Layers, Tag, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { listEntities } from "@/lib/api";

const CONFIG = {
  services: {
    type: "service", title: "Services", singular: "service",
    desc: "Browse every service in the knowledge base and explore how each connects to categories and platforms.",
    Icon: Layers, color: "#16385f", urlPrefix: "service",
  },
  companies: {
    type: "company", title: "Companies & Platforms", singular: "platform",
    desc: "Browse technology companies and platforms, their categories and the services they relate to.",
    Icon: Building2, color: "#0b6b3a", urlPrefix: "company",
  },
  categories: {
    type: "category", title: "Categories", singular: "category",
    desc: "Browse categories that group related services and platforms across the knowledge base.",
    Icon: Tag, color: "#b1560f", urlPrefix: "category",
  },
};

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").concat("#");
const PAGE_SIZE = 60;

export default function ListPage({ kind }) {
  const cfg = CONFIG[kind];
  const [params, setParams] = useSearchParams();
  const letter = params.get("letter") || "";
  const q = params.get("q") || "";
  const mainOnly = params.get("main") === "1";
  const page = parseInt(params.get("page") || "1", 10);
  const [term, setTerm] = useState(q);

  useEffect(() => { setTerm(q); }, [q]);
  useEffect(() => { window.scrollTo(0, 0); }, [kind, letter, page, q, mainOnly]);

  const { data, isFetching } = useQuery({
    queryKey: ["list", cfg.type, { letter, q, page, mainOnly }],
    queryFn: () =>
      listEntities(cfg.type, {
        page, limit: PAGE_SIZE,
        ...(letter ? { letter } : {}),
        ...(q ? { q } : {}),
        ...(mainOnly && kind === "categories" ? { main_only: true } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, v);
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <Seo
        title={`${cfg.title} — CompuBrain Knowledge Base`}
        description={cfg.desc}
        keywords={`${cfg.title}, CompuBrain, digital services, technology platforms, categories`}
        path={`/${kind}`}
      />
      <div className="border-b border-[var(--line)] bg-[var(--bg-alt)]">
        <div className="mx-auto max-w-[1240px] px-4 py-5 lg:px-6">
          <Breadcrumbs items={[{ name: "Home", type: "home" }, { name: cfg.title }]} />
          <div className="mt-3 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-[6px] text-white" style={{ background: cfg.color }}>
              <cfg.Icon size={20} />
            </span>
            <div>
              <h1 className="text-3xl font-bold text-[var(--navy)]" data-testid="list-title">{cfg.title}</h1>
              <p className="text-sm text-[var(--muted)]">{cfg.desc}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-6 lg:px-6">
        {/* controls */}
        <div className="mb-4 flex flex-col gap-3">
          <form
            onSubmit={(e) => { e.preventDefault(); update({ q: term, letter: "", page: "" }); }}
            className="flex gap-2"
          >
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={`Filter ${cfg.title.toLowerCase()} by name…`}
              data-testid="list-filter-input"
              className="w-full max-w-md rounded-[4px] border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--gov-blue)]"
            />
            <button className="rounded-[4px] bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--gov-blue)]" data-testid="list-filter-submit">
              Filter
            </button>
            {(q || letter || mainOnly) && (
              <button
                type="button"
                onClick={() => { setTerm(""); setParams(new URLSearchParams()); }}
                className="rounded-[4px] border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:bg-[var(--bg-alt)]"
                data-testid="list-clear"
              >
                Clear
              </button>
            )}
          </form>

          <div className="flex flex-wrap items-center gap-1">
            {kind === "categories" && (
              <button
                onClick={() => update({ main: mainOnly ? "" : "1", letter: "", q: "" })}
                className={`mr-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${mainOnly ? "bg-[var(--navy)] text-white" : "border border-[var(--line)] text-[var(--ink-soft)] hover:bg-[var(--bg-alt)]"}`}
                data-testid="filter-main-only"
              >
                Main only
              </button>
            )}
            <button
              onClick={() => update({ letter: "", q: "" })}
              className={`rounded px-2.5 py-1 text-sm font-semibold ${!letter ? "bg-[var(--bg-panel)] text-[var(--navy)]" : "text-[var(--link)] hover:bg-[var(--bg-alt)]"}`}
            >
              All
            </button>
            {LETTERS.map((l) => (
              <button
                key={l}
                onClick={() => update({ letter: l, q: "" })}
                className={`rounded px-2.5 py-1 text-sm font-semibold ${letter === l ? "bg-[var(--navy)] text-white" : "text-[var(--link)] hover:bg-[var(--bg-alt)]"}`}
                data-testid={`letter-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <p className="mb-3 text-sm text-[var(--muted)]" data-testid="list-count">
          {isFetching ? "Loading…" : `${total} ${total === 1 ? cfg.singular : cfg.title.toLowerCase()} found`}
        </p>

        {items.length === 0 && !isFetching ? (
          <div className="rounded-[6px] border border-[var(--line)] bg-white py-16 text-center text-[var(--muted)]" data-testid="list-empty">
            No {cfg.title.toLowerCase()} match your filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="list-grid">
            {items.map((it) => (
              <Link
                key={it.slug}
                to={`/${cfg.urlPrefix}/${it.slug}`}
                data-testid={`list-item-${it.slug}`}
                className="card-hover group rounded-[5px] border border-[var(--line)] bg-white p-3.5 no-underline"
              >
                <span className="flex items-center gap-1.5">
                  {it.is_main && (
                    <span className="rounded bg-[#fdf0e3] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#8a4b0f]">Main</span>
                  )}
                  <span className="text-[15px] font-bold text-[var(--ink)] group-hover:text-[var(--gov-blue)]">{it.name}</span>
                </span>
                {it.main_category && (
                  <span className="mt-0.5 block text-xs text-[var(--muted)]">{it.main_category}</span>
                )}
                {it.group && (
                  <span className="mt-0.5 block text-xs text-[var(--muted)]">{it.group}</span>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* pagination */}
        {pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
            <button
              disabled={page <= 1}
              onClick={() => update({ page: String(page - 1) })}
              className="flex items-center gap-1 rounded-[4px] border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-40"
              data-testid="page-prev"
            >
              <ChevronLeft size={15} /> Prev
            </button>
            <span className="px-3 text-sm text-[var(--ink-soft)]">Page {page} of {pages}</span>
            <button
              disabled={page >= pages}
              onClick={() => update({ page: String(page + 1) })}
              className="flex items-center gap-1 rounded-[4px] border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-40"
              data-testid="page-next"
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
